import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  CounterOfferDto,
  NegotiationHistoryDto,
  CounterOfferRequestDto
} from '@umss/shared/data-access';
import { NEGOTIATION_SERVICE } from '@umss/shared/data-access';
import { UmssButtonComponent } from '../button/button.component';
import { UmssInputComponent } from '../input/input.component';
import { UmssTextareaComponent } from '../textarea/textarea.component';
import { UmssDialogHeaderComponent } from '../dialog-header/dialog-header.component';
import { UmssDialogFooterComponent } from '../dialog-footer/dialog-footer.component';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';
import { UmssAlertComponent } from '../alert/alert.component';
import { UmssBadgeComponent } from '../badge/badge.component';
import { UmssEmptyStateComponent } from '../empty-state/empty-state.component';
import { UmssCardComponent } from '../card/card.component';
import { collapseInOut, fadeIn } from '../../animations';
import { SmoothCollapseDirective } from '../../directives/smooth-collapse.directive';

type CounterOfferFormGroup = FormGroup<{
  proposedRate: FormControl<number | null>;
  message: FormControl<string>;
}>;

@Component({
  selector: 'umss-counteroffer-history',
  templateUrl: './counteroffer-history.component.html',
  styleUrls: ['./counteroffer-history.component.scss'],
  standalone: true,
  animations: [fadeIn, collapseInOut],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UmssButtonComponent,
    UmssInputComponent,
    UmssTextareaComponent,
    UmssDialogHeaderComponent,
    UmssDialogFooterComponent,
    UmssSkeletonComponent,
    UmssAlertComponent,
    UmssBadgeComponent,
    UmssEmptyStateComponent,
    UmssCardComponent,
    SmoothCollapseDirective
  ]
})
export class UmssCounterofferHistoryComponent {
  private negotiationService = inject(NEGOTIATION_SERVICE);

  shiftRequestId = input.required<string>();
  openedBy = input.required<'contractor' | 'facility'>();

  /** If true (default), renders a dialog-header with close button. Set false for inline embedding. */
  isStandaloneDialog = input(true);

  /** Emitted when the current user successfully accepts the other party's offer. */
  accepted = output<void>();

  /** Emitted when the user clicks the close button (standalone mode only). */
  closed = output<void>();

  readonly negotiation = signal<NegotiationHistoryDto | null>(null);
  readonly isLoading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isSubmitting = signal(false);
  readonly isShowingCounterForm = signal(false);

  /** The role that made the most recent counter-offer, or null if no offers yet. */
  readonly lastOfferBy = computed<'contractor' | 'facility' | null>(() => {
    const offers = this.negotiation()?.offers;
    if (!offers?.length) return null;
    return offers[offers.length - 1].proposedByRole;
  });

  /**
   * True when it is this user's turn to act (last offer came from the other party)
   * and negotiation is still active.
   */
  readonly isMyTurn = computed(
    () =>
      this.negotiation()?.isNegotiationActive === true &&
      this.lastOfferBy() !== null &&
      this.lastOfferBy() !== this.openedBy()
  );

  /** True when the current user is waiting for the other party's reply. */
  readonly isWaitingForOtherParty = computed(
    () =>
      this.negotiation()?.isNegotiationActive === true &&
      this.lastOfferBy() === this.openedBy()
  );

  readonly counterForm: CounterOfferFormGroup = new FormGroup({
    proposedRate: new FormControl<number | null>(null, [
      Validators.required,
      Validators.min(1),
      Validators.max(500)
    ]),
    message: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)]
    })
  });

  get messageMaxLength(): number | undefined {
    const ctrl = this.counterForm.controls.message;
    const errors = ctrl.validator?.({
      value: 'x'.repeat(99999)
    } as AbstractControl);
    return errors?.['maxlength']?.requiredLength ?? undefined;
  }

  get proposedRateError(): string | null {
    const ctrl = this.counterForm.controls.proposedRate;
    if (!ctrl.touched || ctrl.valid) return null;
    if (ctrl.hasError('required')) return 'Rate is required.';
    if (ctrl.hasError('min')) return 'Rate must be at least $1.00/hr.';
    if (ctrl.hasError('max')) return 'Rate cannot exceed $500.00/hr.';
    return null;
  }

  get messageError(): string | null {
    const ctrl = this.counterForm.controls.message;
    if (!ctrl.touched || ctrl.valid) return null;
    if (ctrl.hasError('maxlength'))
      return `Message cannot exceed ${this.messageMaxLength} characters.`;
    return null;
  }

  get otherPartyLabel(): string {
    return this.openedBy() === 'facility' ? 'Contractor' : 'Facility';
  }

  // --- Footer button state (standalone mode) ---

  readonly footerPrimaryLabel = computed<string | null>(() => {
    if (!this.isMyTurn()) return null;
    return this.isShowingCounterForm() ? 'Send proposal' : 'Accept proposal';
  });

  readonly footerPrimaryIcon = computed<string | undefined>(() => {
    if (!this.isMyTurn()) return undefined;
    return this.isShowingCounterForm()
      ? 'phosphorPaperPlaneTilt'
      : 'phosphorCheck';
  });

  readonly footerSecondaryLabel = computed<string>(() => {
    if (!this.isMyTurn()) return 'Close';
    return this.isShowingCounterForm() ? 'Cancel' : 'Counter-offer';
  });

  readonly footerSecondaryIcon = computed<string | undefined>(() => {
    if (this.isMyTurn() && !this.isShowingCounterForm())
      return 'phosphorArrowsLeftRight';
    return undefined;
  });

  onFooterPrimaryClick(): void {
    if (!this.isMyTurn()) return;
    if (this.isShowingCounterForm()) {
      this.onSubmitCounterOffer();
    } else {
      this.onAccept();
    }
  }

  onFooterSecondaryClick(): void {
    if (this.isMyTurn()) {
      this.onToggleCounterForm();
    } else {
      this.closed.emit();
    }
  }

  constructor() {
    effect(() => {
      this.loadHistory(this.shiftRequestId());
    });
  }

  labelFor(offer: CounterOfferDto): string {
    return offer.proposedByRole === this.openedBy()
      ? 'You'
      : this.otherPartyLabel;
  }

  onAccept(): void {
    this.isSubmitting.set(true);
    this.negotiationService.acceptLatestOffer(this.shiftRequestId()).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.accepted.emit();
      },
      error: () => {
        this.isSubmitting.set(false);
        this.error.set('Failed to accept the proposal. Please try again.');
      }
    });
  }

  onToggleCounterForm(): void {
    this.isShowingCounterForm.update((v) => !v);
    if (!this.isShowingCounterForm()) {
      this.counterForm.reset();
    }
  }

  onSubmitCounterOffer(): void {
    this.counterForm.markAllAsTouched();
    if (this.counterForm.invalid) return;

    const { proposedRate, message } = this.counterForm.getRawValue();
    if (proposedRate === null) return;

    const body: CounterOfferRequestDto = {
      proposedRate,
      message: message || undefined
    };

    this.isSubmitting.set(true);
    this.negotiationService
      .submitCounterOffer(this.shiftRequestId(), body)
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.isShowingCounterForm.set(false);
          this.counterForm.reset();
          this.loadHistory(this.shiftRequestId());
        },
        error: () => {
          this.isSubmitting.set(false);
          this.error.set('Failed to submit counter-offer. Please try again.');
        }
      });
  }

  private loadHistory(requestId: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.negotiationService.getNegotiationHistory(requestId).subscribe({
      next: (data) => {
        this.negotiation.set(data);
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('Failed to load negotiation history.');
        this.isLoading.set(false);
      }
    });
  }
}
